import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PackagePlus, Loader2, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";


type PecaLookup = {
  id: string;
  codigo?: string | null;
  descricao: string;
  marca?: string | null;
  unidade?: string | null;
  estoqueAtual?: any;
  estoqueMin?: any;
  precoVenda?: any;
};

function onlyNumberDotComma(s: string) {
  return s.replace(/[^\d.,-]/g, "");
}
function normalizeDecimalInput(s: string) {
  const cleaned = onlyNumberDotComma(s).trim();
  if (!cleaned) return "";
  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");
  if (hasComma && hasDot) return cleaned.replace(/\./g, "").replace(",", ".");
  if (hasComma && !hasDot) return cleaned.replace(",", ".");
  return cleaned;
}
function money(v: any) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function EstoqueMovimentar() {
  const [q, setQ] = useState("");
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [options, setOptions] = useState<PecaLookup[]>([]);
  const [selected, setSelected] = useState<PecaLookup | null>(null);

  const [tipo, setTipo] = useState<"entrada" | "saida" | "ajuste">("entrada");
  const [qtd, setQtd] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const qtdN = useMemo(() => normalizeDecimalInput(qtd), [qtd]);

  const [searchParams] = useSearchParams();

  async function doLookup(term: string) {
    const t = term.trim();
    if (!t) {
      setOptions([]);
      return;
    }
    setLoadingLookup(true);
    try {
      const { data } = await api.get("/api/pecas/lookup", {
        params: { q: t, limit: 10 },
      });
      setOptions(data?.data || []);
    } catch (err: any) {
      toast.error("Erro ao buscar peças");
    } finally {
      setLoadingLookup(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => doLookup(q), 250);
    return () => clearTimeout(t);

  }, [q]);


  useEffect(() => {
    const pecaId = searchParams.get("pecaId");
    if (pecaId) {
      loadPecaById(pecaId);
    }

  }, [searchParams]);



  async function loadPecaById(pecaId: string) {
    setLoadingLookup(true);
    try {
      const { data } = await api.get(`/api/pecas/${pecaId}`);
      const p = data?.peca;

      if (!p?.id) {
        toast.error("Peça não encontrada");
        return;
      }

      // monta o "selected" no mesmo formato do lookup
      const selectedPeca = {
        id: p.id,
        codigo: p.codigo,
        descricao: p.descricao,
        marca: p.marca,
        unidade: p.unidade,
        estoqueAtual: p.estoqueAtual,
        estoqueMin: p.estoqueMin,
        precoVenda: p.precoVenda,
      };

      setSelected(selectedPeca);
      setOptions([]);

      // Preenche a busca só para mostrar o que foi carregado
      setQ(p.codigo ? `${p.codigo} - ${p.descricao}` : p.descricao);
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Erro ao carregar peça";
      toast.error(msg);
    } finally {
      setLoadingLookup(false);
    }
  }

  async function onMovimentar(e: React.FormEvent) {
    e.preventDefault();
    if (!selected?.id) return toast.error("Selecione uma peça");
    if (!qtdN || Number(qtdN) <= 0) return toast.error("Informe uma quantidade maior que zero");

    setSaving(true);
    try {
      const payload = {
        pecaId: selected.id,
        tipo,
        qtd: qtdN,
        motivo: motivo.trim() ? motivo.trim() : null,
      };

      const { data } = await api.post("/api/estoque/movimentar", payload);

      const novo = data?.peca?.estoqueAtual;
      const lowStock = !!data?.lowStock;

      toast.success(
        lowStock
          ? `Movimento lançado ✅ (Estoque baixo!) Novo: ${novo}`
          : `Movimento lançado ✅ Novo estoque: ${novo}`
      );

      // atualiza card local
      setSelected((prev) => (prev ? { ...prev, estoqueAtual: novo } : prev));

      // limpa campos de movimento
      setQtd("");
      setMotivo("");
      setTipo("entrada");
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Erro ao movimentar estoque";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PackagePlus size={20} />
            Movimentar estoque
          </h1>
          <p className="text-sm text-slate-500">
            Registra histórico e atualiza o estoque atual da peça.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5 space-y-6">
          {/* Busca */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Buscar peça</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setSelected(null);
                  }}
                  placeholder="Digite código, descrição, marca..."
                  className="pl-9"
                />

              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelected(null);
                  setQ("");
                  setOptions([]);
                }}
              >
                Trocar peça
              </Button>


              {/* Dropdown simples */}
              {q.trim() && !selected && (
                <div className="border rounded-md mt-2 overflow-hidden">
                  {loadingLookup ? (
                    <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                      <Loader2 className="animate-spin" size={16} /> Buscando...
                    </div>
                  ) : options.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500">Nenhuma peça encontrada</div>
                  ) : (
                    <div className="max-h-64 overflow-auto">
                      {options.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setSelected(p);
                            setQ(p.codigo ? `${p.codigo} - ${p.descricao}` : p.descricao);
                            setOptions([]);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {p.codigo ? `${p.codigo} · ` : ""}{p.descricao}
                          </div>
                          <div className="text-xs text-slate-500">
                            {p.marca || "-"} · Un: {p.unidade || "un"} · Estoque: {String(p.estoqueAtual ?? "0")}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Card da peça selecionada */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">Peça selecionada</div>

              {selected ? (
                <div className="mt-3 space-y-1 text-sm text-slate-700">
                  <div><b>Descrição:</b> {selected.descricao}</div>
                  <div><b>Código:</b> {selected.codigo || "-"}</div>
                  <div><b>Unidade:</b> {selected.unidade || "un"}</div>
                  <div><b>Estoque atual:</b> {String(selected.estoqueAtual ?? "0")}</div>
                  <div><b>Estoque mínimo:</b> {String(selected.estoqueMin ?? "0")}</div>
                  <div><b>Preço venda:</b> {money(selected.precoVenda ?? 0)}</div>
                </div>
              ) : (
                <div className="mt-2 text-sm text-slate-500">
                  Pesquise e selecione uma peça para movimentar.
                </div>
              )}
            </div>
          </div>

          {/* Form Movimento */}
          <form onSubmit={onMovimentar} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <select
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value as any)}
                  className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                  <option value="ajuste">Ajuste (define estoque)</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Quantidade</Label>
                <Input
                  value={qtd}
                  onChange={(e) => setQtd(e.target.value)}
                  placeholder={tipo === "ajuste" ? "Ex: 10 (vira estoque atual)" : "Ex: 2"}
                  inputMode="decimal"
                />
                <p className="text-xs text-slate-500">
                  {tipo === "saida"
                    ? "Saída não permite estoque negativo."
                    : tipo === "ajuste"
                      ? "Ajuste substitui o estoque atual pelo valor informado."
                      : "Entrada soma ao estoque atual."}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Motivo (opcional)</Label>
                <Input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex: Compra, devolução, inventário..."
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={saving || !selected || !qtdN || Number(qtdN) <= 0}
                className="gap-2"
              >
                <PackagePlus size={16} />
                {saving ? "Lançando..." : "Lançar movimento"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  );
}
